/**
 * EventIdPromotions — records the optimistic→server id re-key of a ghost
 * promotion (`EventOps.create` confirming an `insertOptimistic` placeholder)
 * so id-keyed DOM reconcilers can treat the re-key as a RENAME of the live
 * ghost element instead of a teardown + fresh mount.
 *
 * Why: the data layer already promotes atomically (the overlay projector's
 * confirm re-keys placeholder → canonical in one derivation), but the
 * invalidation it emits carries no id mapping — a renderer that keys DOM
 * nodes by event id (the timed-span reconciler, `takeSpanCell`) sees
 * remove(optimistic) + add(server) and tears the ghost node down, which can
 * blank a frame while the replacement waits behind `pending-layout` for its
 * batched tiling mutate. Consulting this registry at the keyed-reuse seam
 * lets the ghost's element be re-keyed in place — one element whose identity
 * survives the promotion. This is the event analog of the COW promote's
 * serial/rekey rule (requirement
 * `optimistic-soundness/event-create-promotes-ghost-in-place`).
 *
 * Scoped per client: each client's `EventsTable` owns one, so two clients in
 * one process never see each other's promotions. A renderer reaches its
 * client's registry through the read/consume view on `@tento/lona/platform`
 * (`eventIdPromotionsOf(row)`).
 *
 * Entries are short-lived by design: the promoting repaint follows the
 * confirm within the same invalidation turn, so a small TTL + FIFO cap
 * bounds the map and expires entries no repaint consumed (e.g. a confirm
 * that landed while a different view was active).
 */

import type { Option } from "@tento-lona/sheets";
import { EventId } from "@tento-lona/sheets";

const CAP = 64;
const TTL_MS = 10_000;

type Promotion = { sourceId: EventId; at: number };

/** One client's record of created events' optimistic→server id re-keys, for
 *  keyed DOM reuse (see the module comment). */
export class EventIdPromotions implements EventIdPromotions.Surface {
  /** settledId (server canonical) → the optimistic id it was promoted from. */
  readonly #bySettledId = new Map<EventId, Promotion>();

  /** Record `sourceId` (the optimistic placeholder id) being re-keyed to
   *  `settledId` (the server canonical id). Called by `EventOps.create` at
   *  the moment it confirms the placeholder — before the confirm's
   *  invalidation, so the promoting repaint can already see the mapping. */
  record(sourceId: EventId, settledId: EventId): void {
    this.#bySettledId.delete(settledId);
    this.#bySettledId.set(settledId, { sourceId, at: Date.now() });
    if (this.#bySettledId.size > CAP) {
      const oldest = this.#bySettledId.keys().next().value;
      if (oldest != null) this.#bySettledId.delete(oldest);
    }
  }

  sourceIdFor(settledId: EventId): Option<EventId> {
    const promotion = this.#bySettledId.get(settledId);
    if (!promotion) return null;
    if (Date.now() - promotion.at > TTL_MS) {
      this.#bySettledId.delete(settledId);
      return null;
    }
    return promotion.sourceId;
  }

  consume(settledId: EventId): void {
    this.#bySettledId.delete(settledId);
  }
}

/** Types that go with `EventIdPromotions`: `Surface`. */
export namespace EventIdPromotions {
  /** What a renderer may do with its client's promotions: read and consume. */
  export interface Surface {
    /** The optimistic id `settledId` was promoted from, when the promotion is
     *  recent (TTL) and unconsumed; null otherwise. */
    sourceIdFor(settledId: EventId): Option<EventId>;
    /** Consume the promotion once a reconciler has resolved it (renamed the
     *  live element, or established that no ghost element is live) — later
     *  paints resolve the settled id directly off the DOM. */
    consume(settledId: EventId): void;
  }
}
